import { create } from "zustand";
import type { PlayerSource, PlayerStatus } from "@hybrid-iptv/player";
import { resolveStreamUrl } from "./streamToken.js";
import { usePlayerStore } from "./playerStore.js";
import { MAX_MULTIVIEW_TILES } from "./multiviewLimit.js";
import { requireFeature } from "../library/proFeatures.js";

/**
 * Section 14 step 3 — Multiview grid.
 *
 * Every tile is an INDEPENDENT HlsPlayer instance on its own <video> element,
 * and every playing tile holds exactly ONE provider connection (a gate slot
 * for the life of its upstream fetch). The connection budget is the hard
 * constraint here: 4 is the app's absolute max — the client caps the grid at
 * MAX_MULTIVIEW_TILES (4) and the server gate (STREAM_PROXY_MAX_CONNECTIONS)
 * defaults to the same 4. Exit destroys the tile players before the primary
 * hands off, so the count never exceeds the grid.
 *
 * Only the PRIMARY tile is audible; secondary tiles are muted (also what lets
 * them autoplay under browser policy). On exit the primary tile's source is
 * handed back to the main player (playerStore), so the channel keeps playing
 * in the corner mini-player — one connection total afterwards.
 */

/** A channel the multiview picker can add. Same shape as RecentChannel. */
export interface MultiviewChannel {
  id: string;
  name: string;
  number?: number | null;
  logoUrl?: string | null;
  streamUrl: string;
  altStreamUrls?: string[];
}

export interface MultiviewTile {
  id: string;
  channel: MultiviewChannel;
  /** Same-origin tokenized URL + metadata. Resolved BEFORE the tile renders so
   *  the tile's player can load immediately (one connection per tile). */
  source: PlayerSource;
  status: PlayerStatus;
  error?: string;
  /** Secondary tiles are muted so autoplay is never blocked and only the
   *  primary tile is audible. */
  muted: boolean;
}

/** Hard cap on simultaneous tiles — the connection budget. */
export { MAX_MULTIVIEW_TILES };

interface MultiviewState {
  active: boolean;
  tiles: MultiviewTile[];
  primaryId: string | null;
  /**
   * How many tiles fit right now. MAX_MULTIVIEW_TILES on the website; in the
   * Android app, what the device, the provider's limit, other devices and
   * recordings leave (multiviewLimit.ts) — null while that is still being asked.
   */
  limit: number | null;
  /** Why the grid is full, for the picker. */
  limitNote: string | null;
  /** A one-off message, e.g. a tile removed because a recording started. */
  notice: string | null;
  setLimit: (limit: number | null, note: string | null) => void;
  /**
   * A recording started and the grid is now over the limit: recordings win, so
   * the newest non-main tiles go, with a notice naming them.
   */
  enforceLimit: (why: string) => void;
  dismissNotice: () => void;
  /** Start multiview with a single primary tile (entry from the mini/full
   *  dock's Multiview button). Resolves a fresh token for the channel so the
   *  grid owns its own session, then releases the main player's connection. */
  openWith: (channel: MultiviewChannel) => Promise<void>;
  /** Append a muted secondary tile from the picker. No-op at the cap. */
  addChannel: (channel: MultiviewChannel) => Promise<void>;
  /** Remove a tile and release its connection (player destroy is the overlay's
   *  job — it owns the HlsPlayer instances). Removing the primary promotes the
   *  first remaining tile. */
  removeTile: (id: string) => void;
  /** Promote a tile to primary: the old primary mutes, the new one unmutes. */
  setPrimary: (id: string) => void;
  /** Mirror a tile player's statechange into the store for the overlay UI. */
  setTileState: (id: string, patch: { status: PlayerStatus; error?: string }) => void;
  /** Leave multiview. The overlay destroys the tile players (releasing every
   *  connection) and then hands the primary source to the main player. */
  exit: () => void;
}

/**
 * Resolve a channel to a same-origin tokenized source with its fallback list,
 * mirroring playSource's resolution (primary + alts in parallel; a failing alt
 * is dropped, not fatal). Same-origin URLs (already-tokenized) pass through.
 */
async function resolveChannelSource(channel: MultiviewChannel): Promise<PlayerSource> {
  const [resolved, alts] = await Promise.all([
    resolveStreamUrl(channel.streamUrl),
    Promise.all((channel.altStreamUrls ?? []).map((alt) => resolveStreamUrl(alt).catch(() => null))),
  ]);
  const resolvedAlts = alts.filter((alt): alt is NonNullable<typeof alt> => alt !== null);
  return {
    url: resolved.url,
    type: "live",
    title: channel.name,
    altUrls: resolvedAlts.map((alt) => alt.url),
    transcoded: resolved.transcoded,
  };
}

function newTileId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `tile-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const useMultiviewStore = create<MultiviewState>()((set, get) => ({
  active: false,
  tiles: [],
  primaryId: null,
  limit: MAX_MULTIVIEW_TILES,
  limitNote: null,
  notice: null,

  setLimit: (limit, note) => set({ limit, limitNote: note }),

  enforceLimit: (why) => {
    const { tiles, primaryId, limit } = get();
    if (limit === null || tiles.length <= limit) return;
    const removable = tiles.filter((tile) => tile.id !== primaryId);
    const drop = removable.slice(-(tiles.length - limit));
    if (drop.length === 0) return;
    const dropIds = new Set(drop.map((tile) => tile.id));
    set({
      tiles: tiles.filter((tile) => !dropIds.has(tile.id)),
      notice: `Removed ${drop.map((tile) => tile.channel.name).join(", ")}: ${why}`,
    });
  },

  dismissNotice: () => set({ notice: null }),

  openWith: async (channel) => {
    if (get().active) return;
    if (!requireFeature("multiview")) return;
    const source = await resolveChannelSource(channel);
    const id = newTileId();
    set({
      active: true,
      tiles: [{ id, channel, source, status: "loading", muted: false }],
      primaryId: id,
    });
    // The channel now lives in the primary tile; release the main player's
    // connection so we never hold two sessions for the same channel.
    usePlayerStore.getState().close();
  },

  addChannel: async (channel) => {
    const { active, tiles } = get();
    const limit = get().limit;
    if (!active || limit === null || tiles.length >= Math.min(limit, MAX_MULTIVIEW_TILES)) return;
    const source = await resolveChannelSource(channel);
    const id = newTileId();
    set({
      tiles: [...tiles, { id, channel, source, status: "loading", muted: true }],
    });
  },

  removeTile: (id) => {
    const { tiles, primaryId } = get();
    let next = tiles.filter((tile) => tile.id !== id);
    if (next.length === 0) {
      set({ tiles: [], primaryId: null, active: false });
      return;
    }
    let nextPrimary = primaryId;
    if (primaryId === id) {
      // Promote the first remaining tile and unmute it.
      nextPrimary = next[0]!.id;
      next = next.map((tile) => (tile.id === nextPrimary ? { ...tile, muted: false } : tile));
    }
    set({ tiles: next, primaryId: nextPrimary });
  },

  setPrimary: (id) => {
    const { tiles, primaryId } = get();
    if (!tiles.some((tile) => tile.id === id) || primaryId === id) return;
    set({
      primaryId: id,
      tiles: tiles.map((tile) => ({ ...tile, muted: tile.id !== id })),
    });
  },

  setTileState: (id, patch) => {
    set((state) => ({
      tiles: state.tiles.map((tile) => (tile.id === id ? { ...tile, ...patch } : tile)),
    }));
  },

  exit: () => {
    set({ active: false, tiles: [], primaryId: null, notice: null });
  },
}));

/**
 * Build the MultiviewChannel for the channel currently playing in the main
 * player (used by the dock's Multiview button). Prefers the recent-history
 * entry (it carries the ORIGINAL provider URL, so the tile gets a fresh
 * token); falls back to the player's already-tokenized URL when the current
 * source has no matching recent entry (resolveStreamUrl passes same-origin
 * URLs through unchanged).
 */
export function multiviewChannelFromCurrentPlayer(): MultiviewChannel | null {
  const { source, recentChannels } = usePlayerStore.getState();
  if (!source) return null;
  const match = recentChannels.find((c) => c.name === source.title);
  if (match) {
    return {
      id: match.id,
      name: match.name,
      number: match.number,
      logoUrl: match.logoUrl,
      streamUrl: match.streamUrl,
      altStreamUrls: match.altStreamUrls,
    };
  }
  return {
    id: source.title ?? "live",
    name: source.title ?? "Live",
    streamUrl: source.url,
    altStreamUrls: source.altUrls,
  };
}
